import { ColorPicker } from '@/components/inputs/ColorPicker';
import { Popover } from '@/components/utils/Popover/Popover';
import { FONT_FAMILIES } from '@/constant';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import RoundedCornerIcon from '@mui/icons-material/RoundedCorner';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import { Box, IconButton, MenuItem, Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TypeSettingsProps = {
    isPhonetic: boolean;
    fontFamily: string;
    fontSize: number;
    alignment: 'left' | 'center' | 'right';
    formatting: ('bold' | 'italic' | 'underlined')[];
    color: string;
    borderRadius: number;
    backgroundColor: string;
};

interface Props {
    anchorEl: HTMLElement | null;
    value: TypeSettingsProps;
    onClose: () => void;
    onUpdate: (settings: TypeSettingsProps) => void;
    slotProps?: { paper?: { sx?: object; ref: React.Ref<HTMLDivElement> } };
    options?: {
        borderRadius: boolean;
    };
}

export const TypeSettingsPopover: React.FC<Props> = ({ anchorEl, onClose, onUpdate, value, slotProps, options }) => {
    const { t } = useTranslation();
    const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
    const [backgroundColorAnchorEl, setBackgroundColorAnchorEl] = useState<HTMLElement | null>(null);
    const colorButtonRef = useRef(null);
    const backgroundColorButtonRef = useRef(null);

    const open = Boolean(anchorEl);

    return (
        <Popover
            title={t('slide.components.assets.text.typeSettings.title')}
            open={open}
            anchorEl={anchorEl}
            onClose={() => onClose()}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: -8,
                horizontal: 'center',
            }}
            slotProps={slotProps}
        >
            <Stack spacing={1}>
                <Box sx={{ display: 'none', alignItems: 'flex-start' }}>
                    <Switch disabled size='small' checked={value.isPhonetic || false} />
                    <Typography variant='body1' sx={{ ml: 1 }}>
                        {t('slide.components.assets.text.typeSettings.phoneticGuide')}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant='body1' sx={{ ml: 1 }}>
                        {t('slide.components.assets.text.typeSettings.text')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Select
                        value={value.fontFamily || FONT_FAMILIES[0]?.fontFamily}
                        onChange={(e) => {
                            const fontFamily = e.target.value;
                            onUpdate?.({ ...value, fontFamily });
                        }}
                        sx={{
                            width: 180,
                        }}
                    >
                        {FONT_FAMILIES.map((font) => (
                            <MenuItem key={font.fontFamily} value={font.fontFamily}>
                                <Tooltip title={t(`slide.components.assets.text.tooltip.font.${font.fontFamily}`, { defaultValue: '' })} placement='right'>
                                    <Box component='span' sx={{ width: '100%', fontFamily: font.fontFamily }}>
                                        {font.name}
                                    </Box>
                                </Tooltip>
                            </MenuItem>
                        ))}
                    </Select>

                    <TextField
                        type='number'
                        size='small'
                        defaultValue={value.fontSize || 16}
                        onChange={(e) => {
                            const fontSize = parseInt(e.target.value);
                            if (isNaN(fontSize) || fontSize <= 0) return;

                            onUpdate?.({ ...value, fontSize });
                        }}
                        slotProps={{ input: { minRows: 8, maxRows: 72 } }}
                        sx={{ width: 72 }}
                    />

                    <Tooltip title={t('slide.components.assets.text.tooltip.color')} placement='top'>
                        <IconButton
                            ref={colorButtonRef}
                            aria-label='text-color'
                            onClick={() => {
                                setColorAnchorEl(colorButtonRef.current);
                            }}
                        >
                            <TextFormatIcon sx={{ color: value.color ?? 'text.primary' }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box>
                    <ToggleButtonGroup
                        value={value.formatting}
                        onChange={(e, newFormats) => {
                            onUpdate?.({ ...value, formatting: newFormats });
                        }}
                        aria-label='text-formatting'
                        fullWidth
                    >
                        <ToggleButton value='bold' aria-label='bold' size='small'>
                            <FormatBoldIcon />
                        </ToggleButton>
                        <ToggleButton value='italic' aria-label='italic' size='small'>
                            <FormatItalicIcon />
                        </ToggleButton>
                        <ToggleButton value='underlined' aria-label='underlined' size='small'>
                            <FormatUnderlinedIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box>
                    <ToggleButtonGroup
                        value={value.alignment}
                        exclusive
                        onChange={(e, newAlignment) => {
                            if (newAlignment !== null) {
                                onUpdate?.({ ...value, alignment: newAlignment });
                            }
                        }}
                        aria-label='text-alignment'
                        fullWidth
                    >
                        <ToggleButton value='left' aria-label='left aligned'>
                            <FormatAlignLeftIcon />
                        </ToggleButton>
                        <ToggleButton value='center' aria-label='centered'>
                            <FormatAlignCenterIcon />
                        </ToggleButton>
                        <ToggleButton value='right' aria-label='right aligned'>
                            <FormatAlignRightIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box>
                    <Typography variant='body1' sx={{ ml: 1 }}>
                        {t('slide.components.assets.text.typeSettings.background')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        disabled={!options?.borderRadius}
                        size='small'
                        value={value.borderRadius ?? 0}
                        type='number'
                        slotProps={{
                            input: {
                                startAdornment: <RoundedCornerIcon sx={{ mr: 1, color: 'action.active' }} />,
                            },
                        }}
                        fullWidth
                        onChange={(e) => {
                            const borderRadius = parseInt(e.target.value);
                            if (isNaN(borderRadius) || borderRadius < 0) return;

                            onUpdate?.({ ...value, borderRadius });
                        }}
                    />
                    <Tooltip title={t('slide.components.assets.text.tooltip.backgroundColor')} placement='top'>
                        <IconButton
                            ref={backgroundColorButtonRef}
                            aria-label='text-backgroundColor'
                            onClick={() => {
                                setBackgroundColorAnchorEl(backgroundColorButtonRef.current);
                            }}
                        >
                            <FormatColorFillIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Stack>

            {colorAnchorEl && (
                <Popover
                    open={Boolean(colorAnchorEl)}
                    anchorEl={colorAnchorEl}
                    onClose={() => setColorAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <ColorPicker
                            value={value.color}
                            onChange={(color: string) => {
                                onUpdate?.({ ...value, color });
                            }}
                        />
                    </Box>
                </Popover>
            )}
            {backgroundColorAnchorEl && (
                <Popover
                    open={Boolean(backgroundColorAnchorEl)}
                    anchorEl={backgroundColorAnchorEl}
                    onClose={() => setBackgroundColorAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <ColorPicker
                            options={{ transparency: true }}
                            value={value.backgroundColor}
                            onChange={(color: string) => {
                                onUpdate?.({ ...value, backgroundColor: color });
                            }}
                        />
                    </Box>
                </Popover>
            )}
        </Popover>
    );
};
